import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Share2, Copy, Users, Gift, TrendingUp, Link as LinkIcon, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Referrals = () => {
  const { token } = useAuth();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferral();
  }, [token]);

  const fetchReferral = async () => {
    try {
      const response = await axios.get(`${API}/referrals/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setReferral(response.data);
      } else {
        // Create one if doesn't exist
        const createResponse = await axios.post(`${API}/referrals/create`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReferral(createResponse.data);
      }
    } catch (error) {
      // Try to create
      try {
        const createResponse = await axios.post(`${API}/referrals/create`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReferral(createResponse.data);
      } catch (e) {
        console.error('Error with referrals:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    const link = `${window.location.origin}/giveaways?ref=${referral?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  const referralLink = referral ? `${window.location.origin}/giveaways?ref=${referral.referral_code}` : '';

  return (
    <div className="space-y-6" data-testid="referrals">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>Share & Earn</h1>
        <p className="text-neutral-600 mt-1">Invite friends and track your referrals</p>
      </div>

      {/* Referral Link Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#FF3B30] to-[#DC2626] rounded-2xl p-6 text-white"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Share2 className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Your Referral Link</h2>
            <p className="text-white/80">Share this link with friends</p>
          </div>
        </div>

        <div className="flex gap-3">
          <Input
            value={referralLink}
            readOnly
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
            data-testid="referral-link-input"
          />
          <Button 
            onClick={copyToClipboard}
            className="bg-white text-[#FF3B30] hover:bg-white/90 rounded-xl"
            data-testid="copy-referral-link"
          >
            {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
          </Button>
        </div>

        <div className="mt-4 p-3 bg-white/10 rounded-xl">
          <p className="text-sm text-white/90">
            <strong>Your Code:</strong> {referral?.referral_code}
          </p>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 border border-neutral-200"
        >
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
            <LinkIcon className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-neutral-900">{referral?.clicks || 0}</p>
          <p className="text-neutral-500">Link Clicks</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 border border-neutral-200"
        >
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-4">
            <Users className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-neutral-900">{referral?.signups || 0}</p>
          <p className="text-neutral-500">Sign Ups</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 border border-neutral-200"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
            <Gift className="w-6 h-6 text-amber-600" />
          </div>
          <p className="text-3xl font-bold text-neutral-900">{referral?.entries || 0}</p>
          <p className="text-neutral-500">Entries Made</p>
        </motion.div>
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-2xl p-6 border border-neutral-200">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>How Referrals Work</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-[#FF3B30]/10 flex items-center justify-center text-[#FF3B30] font-bold text-sm">1</div>
            <div>
              <p className="font-medium text-neutral-900">Share Your Link</p>
              <p className="text-sm text-neutral-500">Send your unique referral link to friends and family</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-[#FF3B30]/10 flex items-center justify-center text-[#FF3B30] font-bold text-sm">2</div>
            <div>
              <p className="font-medium text-neutral-900">They Sign Up & Enter</p>
              <p className="text-sm text-neutral-500">When they use your link to sign up and enter giveaways</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 rounded-full bg-[#FF3B30]/10 flex items-center justify-center text-[#FF3B30] font-bold text-sm">3</div>
            <div>
              <p className="font-medium text-neutral-900">Track Your Impact</p>
              <p className="text-sm text-neutral-500">See your referral stats grow in real-time</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
